export const ACTION_TYPES = {
    serve: ['SQ', 'SM'],
    attack: ['AT', 'AH', 'AQ', 'AM', 'AO'],
    block: ['BT', 'BH', 'BQ', 'BO'],
    reception: ['RQ', 'RM'],
    dig: ['DT', 'DH', 'DQ', 'DO'],
    set: ['ET', 'EH', 'EQ', 'EO'],
    freeball: ['FT', 'FH', 'FQ'],
};

export const POSITION_NAMES = {
    setter: 'Связующий',
    outside: 'Доигровщик',
    opposite: 'Диагональный',
    middle: 'Блокирующий',
    libero: 'Либеро',
};


export function getCurrentSeason(seasons) {
    if (!seasons || seasons.length === 0) return null;
    const active = seasons.find((s) => s.is_active);
    return active || seasons[0];
}

export function getCurrentSeasonMatchIds(seasons, matchesBySeason, matches) {
    const current = getCurrentSeason(seasons);
    if (!current) return matches.map((m) => m.id);
    const group = matchesBySeason.find((s) => s.season_id === current.id);
    return group?.matches || [];
}

export function getDefaultFilters(seasons, matchesBySeason, matches) {
    const season = getCurrentSeason(seasons);
    const matchIds = getCurrentSeasonMatchIds(seasons, matchesBySeason, matches);
    return {
        season_ids: season ? [season.id] : [],
        competition_types: ['championship', 'cup', 'supercup'],
        stages: [],
        match_ids: matchIds,
        action_types: [],
        action_subtypes: [],
        zones: [],
        qualities: [],
        rally_results: [],
        date_from: '',
        date_to: '',
    };
}

export function buildFiltersFromPeriod({ seasonId, competitionTypes, matches }) {
    const filteredMatchIds = matches
        .filter(
            (m) =>
                (!seasonId || m.season_id === seasonId) &&
                competitionTypes.includes(m.competition_type)
        )
        .map((m) => m.id);
    return {
        season_ids: seasonId ? [seasonId] : [],
        competition_types: competitionTypes,
        stages: [],
        match_ids: filteredMatchIds,
        action_types: [],
        action_subtypes: [],
        zones: [],
        qualities: [],
        rally_results: [],
        date_from: '',
        date_to: '',
    };
}

export function getPrimaryRecordKey(position) {
    if (position === 'libero') return 'reception';
    if (position === 'setter') return 'set_efficiency';
    return 'attack_efficiency';
}

export function getPositionKpis(position, stats) {
    const kpis = [];
    const fmtPct = (v) => (v != null ? v + '%' : '—');
    const fmtNum = (v) => (v != null ? String(v) : '—');
    const fmtPerSet = (v) =>
        v != null && typeof v === 'number' ? v.toFixed(2) : v != null ? String(v) : '—';

    if (position === 'libero') {
        kpis.push({
            key: 'reception',
            label: 'Позитивный приём',
            value: fmtPct(stats.reception_positive_pct),
            hint: stats.reception_positive + '/' + stats.reception_total,
        });
        kpis.push({ key: 'dig_eff', label: 'Эффективность защиты', value: fmtPct(stats.dig_efficiency) });
        kpis.push({ key: 'dig', label: 'Успешные защиты', value: fmtNum(stats.dig_success) });
    } else if (position === 'setter') {
        kpis.push({ key: 'set', label: 'Эффективность передачи', value: fmtPct(stats.set_efficiency) });
        kpis.push({ key: 'serve', label: 'Эффективность подачи', value: fmtPct(stats.serve_efficiency) });
        if (stats.attack_total > 0) {
            kpis.push({ key: 'attack', label: 'Очки в атаке', value: fmtNum(stats.attack_kills) });
        }
    } else {
        kpis.push({ key: 'attack', label: 'Эффективность атаки', value: fmtPct(stats.attack_efficiency) });
        kpis.push({ key: 'attack_kps', label: 'Очки за партию', value: fmtPerSet(stats.attack_kills_per_set) });
        kpis.push({ key: 'serve', label: 'Эйсы за партию', value: fmtPerSet(stats.serve_aces_per_set) });
        kpis.push({ key: 'block', label: 'Блок за партию', value: fmtPerSet(stats.block_positive_per_set) });
    }
    return kpis;
}

export function getMatchStatsRows(chartData, matches) {
    return chartData.map((row) => {
        const match = matches.find((m) => m.id === row.match_id);
        return {
            ...row,
            score: match?.score,
            home_team_short: match?.home_team_short,
            away_team_short: match?.away_team_short,
        };
    });
}

export function computeStatsBundle(actions, filters, matches, setsPlayedData, position) {
    const filtered = filterActions(actions, filters);
    const stats = calculateStatsFromActions(filtered, matches, setsPlayedData);
    const chartData = getChartDataFromActions(filtered, matches);
    const records = calculatePersonalRecords(filtered, matches, position);
    return { filtered, stats, chartData, records };
}


export function getEmptyStats() {
    return {
        matches_played: 0,
        sets_played: 0,
        total_actions: 0,
        attack_total: 0,
        attack_kills: 0,
        attack_errors: 0,
        attack_blocked: 0,
        attack_efficiency: 0,
        attack_per_set: 0,
        attack_kills_per_set: 0,
        serve_total: 0,
        serve_aces: 0,
        serve_errors: 0,
        serve_efficiency: 0,
        serve_aces_per_set: 0,
        serve_positive: 0,
        serve_insufficient: 0,
        serve_very_positive: 0,
        block_total: 0,
        block_kills: 0,
        block_positive: 0,
        block_per_set: 0,
        block_positive_per_set: 0,
        block_efficiency: 0,
        reception_total: 0,
        reception_perfect: 0,
        reception_positive: 0,
        reception_positive_only: 0,
        reception_errors: 0,
        reception_neutral: 0,
        reception_poor: 0,
        reception_positive_pct: 0,
        reception_perfect_pct: 0,
        reception_efficiency: 0,
        dig_total: 0,
        dig_success: 0,
        dig_efficiency: 0,
        set_total: 0,
        set_excellent: 0,
        set_positive: 0,
        set_errors: 0,
        set_efficiency: 0,
        freeball_total: 0,
        freeball_perfect: 0,
        freeball_positive: 0,
        freeball_neutral: 0,
        freeball_slash: 0,
        freeball_negative: 0,
        freeball_error: 0,
        freeball_by_quality: null,
    };
}

// Функция фильтрации действий по всем фильтрам
export function filterActions(actions, filters) {
    if (!actions || actions.length === 0) {
        return [];
    }

    let filtered = [...actions];

    // Фильтр по матчам
    if (filters.match_ids && filters.match_ids.length > 0) {
        filtered = filtered.filter(a => filters.match_ids.includes(a.game_id));
    }

    // Фильтр по сезонам
    if (filters.season_ids && filters.season_ids.length > 0) {
        filtered = filtered.filter(a => filters.season_ids.includes(a.season_id));
    }

    // Фильтр по типам соревнований (competition_type)
    if (filters.competition_types && filters.competition_types.length > 0) {
        filtered = filtered.filter(a =>
            a.competition_type && filters.competition_types.includes(a.competition_type)
        );
    }

    // Фильтр по этапам (с правильным маппингом значений из фильтров в значения БД)
    if (filters.stages && filters.stages.length > 0) {
        const stages = [];
        filters.stages.forEach(stage => {
            // Маппинг этапов из фильтров в значения БД
            if (stage === 'championship_preliminary') {
                // Предварительный этап чемпионата = regular-1 или regular-2
                stages.push('regular-1', 'regular-2');
            } else if (stage === 'final_1_4') {
                // Финальный за 1-4 места = final
                stages.push('final');
            } else if (stage === 'final_5_9') {
                // Финальный за 5-9 места = 5-places
                stages.push('5-places');
            } else if (stage === 'cup_additional') {
                // Дополнительный этап кубка = dop
                stages.push('dop');
            } else {
                // Остальные этапы как есть
                stages.push(stage);
            }
        });
        if (stages.length > 0) {
            filtered = filtered.filter(a => a.stage && stages.includes(a.stage));
        }
    }

    // Фильтр по датам
    if (filters.date_from) {
        filtered = filtered.filter(a => a.match_date >= filters.date_from);
    }
    if (filters.date_to) {
        filtered = filtered.filter(a => a.match_date <= filters.date_to);
    }

    // Фильтр по типам действий
    if (filters.action_types && filters.action_types.length > 0) {
        const actionTypes = [];
        filters.action_types.forEach(type => {
            if (ACTION_TYPES[type]) {
                actionTypes.push(...ACTION_TYPES[type]);
            }
        });
        if (actionTypes.length > 0) {
            filtered = filtered.filter(a => actionTypes.includes(a.action_type));
        }
    }

    // Фильтр по подтипам действий
    if (filters.action_subtypes && filters.action_subtypes.length > 0) {
        filtered = filtered.filter(a =>
            a.action_subtype && filters.action_subtypes.includes(a.action_subtype)
        );
    }

    // Фильтр по зонам
    if (filters.zones && filters.zones.length > 0) {
        filtered = filtered.filter(a =>
            a.zone && filters.zones.includes(a.zone)
        );
    }

    // Фильтр по качеству
    if (filters.qualities && filters.qualities.length > 0) {
        filtered = filtered.filter(a =>
            a.quality && filters.qualities.includes(a.quality)
        );
    }

    // Фильтр по результату розыгрыша
    if (filters.rally_results && filters.rally_results.length > 0) {
        filtered = filtered.filter(a =>
            a.rally_result && filters.rally_results.includes(a.rally_result)
        );
    }

    return filtered;
};

// Функция объединения действий по типам (как на бэкенде)
function mergeActions(byType, types) {
    const result = [];
    types.forEach(type => {
        if (byType[type]) {
            result.push(...byType[type]);
        }
    });
    return result;
};

// Функция расчета статистики из отфильтрованных действий
export function calculateStatsFromActions(filteredActions, matches, setsPlayedData = {}) {
    if (!filteredActions || filteredActions.length === 0) {
        return getEmptyStats();
    }

    // Группируем по типам действий
    const byType = {};
    filteredActions.forEach(action => {
        if (!byType[action.action_type]) {
            byType[action.action_type] = [];
        }
        byType[action.action_type].push(action);
    });

    // Уникальные матчи
    const uniqueMatchIds = [...new Set(filteredActions.map(a => a.game_id))];
    const matchCount = uniqueMatchIds.length;

    // Подсчет партий (sets) - используем данные из PlayerMatchStat, если есть
    let setsPlayed = 0;
    if (setsPlayedData && Object.keys(setsPlayedData).length > 0) {
        // Используем данные из PlayerMatchStat для матчей, которые есть в фильтре
        uniqueMatchIds.forEach(matchId => {
            if (setsPlayedData[matchId] && Array.isArray(setsPlayedData[matchId])) {
                setsPlayed += setsPlayedData[matchId].length;
            }
        });
    }

    // Если данных из PlayerMatchStat нет, используем резервный метод
    if (setsPlayed === 0) {
        const setsMap = new Map();
        filteredActions.forEach(action => {
            if (action.set_number) {
                const key = `${action.game_id}_${action.set_number}`;
                setsMap.set(key, true);
            }
        });
        setsPlayed = setsMap.size;
    }

    // Атаки
    const attacks = mergeActions(byType, ['AT', 'AH', 'AQ', 'AM', 'AO']);
    const attackTotal = attacks.length;
    const attackKills = attacks.filter(a => a.quality === '#').length;
    const attackErrors = attacks.filter(a => a.quality === '=').length; // Для атак ошибка = "минус минус"
    const attackBlocked = attacks.filter(a => a.quality === '/').length;
    // Разбивка по качествам для визуализации (как в матче: # + ! - / =)
    const attackByQuality = {
        perfect: attacks.filter(a => a.quality === '#').length,
        positive: attacks.filter(a => a.quality === '+').length,
        neutral: attacks.filter(a => a.quality === '!').length,
        negative: attacks.filter(a => a.quality === '-').length,
        slash: attacks.filter(a => a.quality === '/').length,
        error: attacks.filter(a => a.quality === '=').length,
        blocked: attacks.filter(a => a.quality === '/').length, // алиас для карточек
    };

    // Подачи
    const serves = mergeActions(byType, ['SQ', 'SM']);
    const serveTotal = serves.length;
    // По формуле: ((#+!/)-(=)) / Tot
    // Выиграно: #+!/, проиграно: =
    const serveWon = serves.filter(s => ['#', '+', '!', '/'].includes(s.quality) || s.rally_result === 'p').length;
    const serveLost = serves.filter(s => s.quality === '=').length;
    const serveAces = serves.filter(s => s.quality === '#' || s.rally_result === 'p').length; // Для отображения эйсов
    const serveErrors = serveLost;
    // Дополнительные поля для формулы (как на главной странице)
    const servePositive = serves.filter(s => s.quality === '+').length;
    const serveInsufficient = serves.filter(s => s.quality === '!').length;
    const serveVeryPositive = serves.filter(s => s.quality === '/').length;
    const serveNegative = serves.filter(s => s.quality === '-').length;
    // Разбивка по качествам для визуализации
    const serveByQuality = {
        perfect: serves.filter(s => s.quality === '#' || s.rally_result === 'p').length,
        positive: servePositive,
        insufficient: serveInsufficient,
        veryPositive: serveVeryPositive,
        error: serveErrors,
    };

    // Блоки
    const blocks = mergeActions(byType, ['BT', 'BH', 'BQ', 'BO']);
    const blockTotal = blocks.length;
    const blockKills = blocks.filter(b => b.quality === '#').length;
    const blockSoft = blocks.filter(b => b.quality === '+').length;
    const blockRemain = blocks.filter(b => b.quality === '/').length;
    const blockErrors = blocks.filter(b => b.quality === '=').length;
    // block_positive - блоки с качеством # или + (как на главной странице)
    const blockPositive = blockKills + blockSoft;
    const blockNeutral = blocks.filter(b => b.quality === '!').length;
    const blockNegative = blocks.filter(b => b.quality === '-').length;
    // Разбивка по качествам для визуализации
    const blockByQuality = {
        perfect: blockKills,
        positive: blockSoft,
        invasion: blockRemain,
        error: blockErrors,
    };

    // Приём
    const receptions = mergeActions(byType, ['RQ', 'RM']);
    const receptionTotal = receptions.length;
    const receptionPerfect = receptions.filter(r => r.quality === '#').length;
    const receptionPositiveOnly = receptions.filter(r => r.quality === '+').length;
    const receptionPositive = receptions.filter(r => ['#', '+'].includes(r.quality)).length;
    const receptionErrors = receptions.filter(r => r.quality === '=').length;
    const receptionNegative = receptions.filter(r => r.quality === '-').length;
    // Дополнительные поля для формулы (как на главной странице)
    const receptionNeutral = receptions.filter(r => r.quality === '=').length; // = это ошибка
    const receptionPoor = receptions.filter(r => r.quality === '/').length; // / это плохой прием
    // По формуле: ((#+)-(=/)) / Tot
    // Выиграно: #+, проиграно: =/
    const receptionWon = receptions.filter(r => ['#', '+'].includes(r.quality)).length;
    const receptionLost = receptions.filter(r => ['=', '/'].includes(r.quality)).length;
    // Разбивка по качествам для визуализации
    const receptionByQuality = {
        perfect: receptionPerfect,
        positive: receptionPositiveOnly,
        insufficient: receptions.filter(r => r.quality === '!').length,
        veryPoor: receptionPoor,
        error: receptionNeutral,
    };

    // Защита
    const digs = mergeActions(byType, ['DT', 'DH', 'DQ', 'DO']);
    const digTotal = digs.length;
    const digSuccess = digs.filter(d => ['#', '+', '!'].includes(d.quality)).length;
    const digNegative = digs.filter(d => d.quality === '-').length;
    // По формуле: ((#+/)-(=)) / Tot
    // Выиграно: #+/, проиграно: =
    const digWon = digs.filter(d => ['#', '+', '/'].includes(d.quality)).length;
    const digLost = digs.filter(d => d.quality === '=').length;
    // Разбивка по качествам для визуализации
    const digByQuality = {
        perfect: digs.filter(d => d.quality === '#').length,
        positive: digs.filter(d => d.quality === '+').length,
        veryPositive: digs.filter(d => d.quality === '/').length,
        insufficient: digs.filter(d => d.quality === '!').length,
        error: digs.filter(d => d.quality === '=').length,
    };

    // Передачи
    const sets = mergeActions(byType, ['ET', 'EH', 'EQ', 'EO']);
    const setTotal = sets.length;
    const setExcellent = sets.filter(s => s.quality === '#').length;
    const setPositive = sets.filter(s => ['#', '+'].includes(s.quality)).length;
    const setErrors = sets.filter(s => s.quality === '=').length;
    // По формуле: ((#+)-(/=)) / Tot
    // Выиграно: #+, проиграно: /=
    const setWon = sets.filter(s => ['#', '+'].includes(s.quality)).length;
    const setLost = sets.filter(s => ['/', '='].includes(s.quality)).length;
    // Разбивка по качествам для визуализации
    const setByQuality = {
        perfect: sets.filter(s => s.quality === '#').length,
        positive: sets.filter(s => s.quality === '+').length,
        invasion: sets.filter(s => s.quality === '/').length,
        insufficient: sets.filter(s => s.quality === '!').length,
        negative: sets.filter(s => s.quality === '-').length,
        error: sets.filter(s => s.quality === '=').length,
        other: sets.filter(s => !['#', '+', '/', '!', '-', '='].includes(s.quality)).length,
    };

    // Freeball (FT, FH, FQ) — как в матче, только счёт по качествам
    const freeballs = mergeActions(byType, ['FT', 'FH', 'FQ']);
    const freeballTotal = freeballs.length;
    const freeballByQuality = {
        perfect: freeballs.filter(f => f.quality === '#').length,
        positive: freeballs.filter(f => f.quality === '+').length,
        neutral: freeballs.filter(f => f.quality === '!').length,
        slash: freeballs.filter(f => f.quality === '/').length,
        negative: freeballs.filter(f => f.quality === '-').length,
        error: freeballs.filter(f => f.quality === '=').length,
    };

    return {
        matches_played: matchCount,
        sets_played: setsPlayed,
        total_actions: filteredActions.length,

        // Атаки
        attack_total: attackTotal,
        attack_kills: attackKills,
        attack_errors: attackErrors,
        attack_blocked: attackBlocked,
        attack_efficiency: attackTotal > 0
            ? Math.round(((attackKills - attackErrors - attackBlocked) / attackTotal) * 100 * 10) / 10
            : 0,
        attack_per_set: matchCount > 0
            ? Math.round((attackTotal / (matchCount * 3.5)) * 10) / 10
            : 0,
        // Как на главной странице: очков за партию = kills / sets_played
        attack_kills_per_set: setsPlayed > 0
            ? Math.round((attackKills / setsPlayed) * 100) / 100
            : 0,
        attack_by_quality: attackByQuality,

        // Подачи
        serve_total: serveTotal,
        serve_aces: serveAces,
        serve_errors: serveErrors,
        // Дополнительные поля для формулы (как на главной странице)
        serve_positive: servePositive,
        serve_insufficient: serveInsufficient,
        serve_very_positive: serveVeryPositive,
        // Формула: ((#+!/)-(=)) / Tot
        serve_efficiency: serveTotal > 0
            ? Math.round(((serveWon - serveLost) / serveTotal) * 100 * 10) / 10
            : 0,
        // Как на главной странице: эйсов за партию = aces / sets_played
        serve_aces_per_set: setsPlayed > 0
            ? Math.round((serveAces / setsPlayed) * 100) / 100
            : 0,
        serve_negative: serveNegative,
        serve_by_quality: serveByQuality,

        // Блоки
        block_total: blockTotal,
        block_kills: blockKills,
        block_soft: blockSoft,
        block_remain: blockRemain,
        block_errors: blockErrors,
        // block_positive - блоки с качеством # или + (как на главной странице)
        block_positive: blockPositive,
        block_per_set: matchCount > 0
            ? Math.round((blockTotal / (matchCount * 3.5)) * 10) / 10
            : 0,
        // Как на главной странице: блоков за партию = block_positive / sets_played
        block_positive_per_set: setsPlayed > 0
            ? Math.round((blockPositive / setsPlayed) * 100) / 100
            : 0,
        block_neutral: blockNeutral,
        block_negative: blockNegative,
        // Формула: (# + + - / - =) / total = (block_kills + block_soft - block_remain - block_errors) / block_total
        block_efficiency: blockTotal > 0
            ? Math.round(((blockKills + blockSoft - blockRemain - blockErrors) / blockTotal) * 100 * 10) / 10
            : 0,
        block_by_quality: blockByQuality,

        // Приём
        reception_total: receptionTotal,
        reception_perfect: receptionPerfect,
        reception_positive: receptionPositive,
        // Дополнительные поля для формулы (как на главной странице)
        reception_positive_only: receptionPositiveOnly,
        reception_errors: receptionErrors,
        reception_neutral: receptionNeutral,
        reception_poor: receptionPoor,
        reception_positive_pct: receptionTotal > 0
            ? Math.round((receptionPositive / receptionTotal) * 100 * 10) / 10
            : 0,
        reception_perfect_pct: receptionTotal > 0
            ? Math.round((receptionPerfect / receptionTotal) * 100 * 10) / 10
            : 0,
        // Формула: ((#+)-(=/)) / Tot
        reception_efficiency: receptionTotal > 0
            ? Math.round(((receptionWon - receptionLost) / receptionTotal) * 100 * 10) / 10
            : 0,
        reception_negative: receptionNegative,
        reception_by_quality: receptionByQuality,

        // Защита
        dig_total: digTotal,
        dig_success: digSuccess,
        // Формула: ((#+/)-(=)) / Tot
        dig_efficiency: digTotal > 0
            ? Math.round(((digWon - digLost) / digTotal) * 100 * 10) / 10
            : 0,
        dig_negative: digNegative,
        dig_by_quality: digByQuality,

        // Передачи
        set_total: setTotal,
        set_excellent: setExcellent,
        set_positive: setPositive,
        set_errors: setErrors,
        // Формула: ((#+)-(/=)) / Tot
        set_efficiency: setTotal > 0
            ? Math.round(((setWon - setLost) / setTotal) * 100 * 10) / 10
            : 0,
        set_by_quality: setByQuality,

        // Freeball
        freeball_total: freeballTotal,
        freeball_perfect: freeballByQuality.perfect,
        freeball_positive: freeballByQuality.positive,
        freeball_neutral: freeballByQuality.neutral,
        freeball_slash: freeballByQuality.slash,
        freeball_negative: freeballByQuality.negative,
        freeball_error: freeballByQuality.error,
        freeball_by_quality: freeballByQuality,
    };
};

// Функция получения данных для графика
export function getChartDataFromActions(filteredActions, matches) {
    if (!filteredActions || filteredActions.length === 0) {
        return [];
    }

    // Группируем по матчам
    const byMatch = {};
    filteredActions.forEach(action => {
        if (!byMatch[action.game_id]) {
            byMatch[action.game_id] = [];
        }
        byMatch[action.game_id].push(action);
    });

    const chartData = [];

    Object.keys(byMatch).forEach(matchId => {
        const matchActions = byMatch[matchId];
        const match = matches.find(m => m.id === parseInt(matchId));
        if (!match) return;

        // Группируем по типам
        const byType = {};
        matchActions.forEach(action => {
            if (!byType[action.action_type]) {
                byType[action.action_type] = [];
            }
            byType[action.action_type].push(action);
        });

        // Атаки
        const attacks = mergeActions(byType, ['AT', 'AH', 'AQ', 'AM', 'AO']);
        const attackTotal = attacks.length;
        const attackKills = attacks.filter(a => a.quality === '#').length;
        const attackErrors = attacks.filter(a => a.quality === '=').length;
        const attackBlocked = attacks.filter(a => a.quality === '/').length;

        // Подачи
        const serves = mergeActions(byType, ['SQ', 'SM']);
        const serveTotal = serves.length;
        const serveAces = serves.filter(s => s.quality === '#' || s.rally_result === 'p').length;
        // Формула: ((#+!/)-(=)) / Tot
        const serveWon = serves.filter(s => ['#', '+', '!', '/'].includes(s.quality) || s.rally_result === 'p').length;
        const serveLost = serves.filter(s => s.quality === '=').length;

        // Блоки
        const blocks = mergeActions(byType, ['BT', 'BH', 'BQ', 'BO']);
        const blockTotal = blocks.length;
        const blockKills = blocks.filter(b => b.quality === '#').length;
        const blockSoft = blocks.filter(b => b.quality === '+').length;
        const blockRemain = blocks.filter(b => b.quality === '/').length;
        const blockErrors = blocks.filter(b => b.quality === '=').length;

        // Приём
        const receptions = mergeActions(byType, ['RQ', 'RM']);
        const receptionTotal = receptions.length;
        const receptionPerfect = receptions.filter(r => r.quality === '#').length;
        const receptionPositive = receptions.filter(r => ['#', '+'].includes(r.quality)).length;
        // Формула: ((#+)-(=/)) / Tot
        const receptionWon = receptions.filter(r => ['#', '+'].includes(r.quality)).length;
        const receptionLost = receptions.filter(r => ['=', '/'].includes(r.quality)).length;

        // Находим матч в списке matches или используем данные из действий
        const matchData = matches.find(m => m.id === parseInt(matchId));
        // Используем полные названия команд для красивого отображения
        const matchLabel = matchData
            ? (matchData.home_team || matchData.home_team_short || '') + ' — ' + (matchData.away_team || matchData.away_team_short || '')
            : (matchActions[0]?.home_team_name || matchActions[0]?.home_team_short || '') + ' — ' + (matchActions[0]?.away_team_name || matchActions[0]?.away_team_short || '');

        const matchDate = matchData?.date_full || matchData?.date || matchActions[0]?.match_date || '';

        chartData.push({
            match_id: parseInt(matchId),
            date: matchDate ? matchDate.split('-').reverse().slice(0, 2).join('.') : '', // Формат d.m
            date_full: matchDate, // Формат Y-m-d
            match_date: matchDate, // Для совместимости
            match_label: matchLabel,
            attack_kills: attackKills,
            attack_efficiency: attackTotal > 0
                ? Math.round(((attackKills - attackErrors - attackBlocked) / attackTotal) * 100 * 10) / 10
                : 0,
            serve_aces: serveAces,
            serve_efficiency: serveTotal > 0
                ? Math.round(((serveWon - serveLost) / serveTotal) * 100 * 10) / 10
                : 0,
            block_kills: blockKills,
            block_efficiency: blockTotal > 0
                ? Math.round(((blockKills + blockSoft - blockRemain - blockErrors) / blockTotal) * 100 * 10) / 10
                : 0,
            reception_perfect_pct: receptionTotal > 0
                ? Math.round((receptionPerfect / receptionTotal) * 100 * 10) / 10
                : 0,
            reception_positive_pct: receptionTotal > 0
                ? Math.round((receptionPositive / receptionTotal) * 100 * 10) / 10
                : 0,
            reception_efficiency: receptionTotal > 0
                ? Math.round(((receptionWon - receptionLost) / receptionTotal) * 100 * 10) / 10
                : 0,
            total_points: attackKills + serveAces + blockKills,
        });
    });

    // Сортируем по дате
    chartData.sort((a, b) => {
        const dateA = new Date(a.match_date);
        const dateB = new Date(b.match_date);
        return dateA - dateB;
    });

    return chartData;
};

// Функция расчета личных рекордов
export function calculatePersonalRecords(filteredActions, matches, positionSlug) {
    if (!filteredActions || filteredActions.length === 0) {
        return {
            best_match_points: null,
            best_match_efficiency: null,
            best_match_reception: null,
            best_match_serve: null,
        };
    }

    // Группируем действия по матчам
    const byMatch = {};
    filteredActions.forEach(action => {
        if (!byMatch[action.game_id]) {
            byMatch[action.game_id] = [];
        }
        byMatch[action.game_id].push(action);
    });

    // Рекорды для каждого типа элемента
    let bestMatchAttack = null; // По очкам в атаке
    let bestMatchAttackEff = null; // По эффективности атаки
    let bestMatchServe = null; // По эйсам
    let bestMatchServeEff = null; // По эффективности подачи
    let bestMatchReception = null; // По проценту позитивных
    let bestMatchReceptionPerfect = null; // По проценту отличных
    let bestMatchReceptionEff = null; // По эффективности приема
    let bestMatchBlock = null; // По очкам на блоке
    let bestMatchBlockEff = null; // По эффективности блока
    let bestMatchDig = null; // По успешным защитам
    let bestMatchDigEff = null; // По эффективности защиты
    let bestMatchSetEff = null; // По эффективности передач
    let bestMatchTotalPoints = null; // По набранным очкам

    let maxAttackPoints = 0;
    let maxAttackEfficiency = -100;
    let maxServeAces = 0;
    let maxServeEfficiency = -100;
    let maxReceptionPct = 0;
    let maxReceptionPerfectPct = 0;
    let maxReceptionEfficiency = -100;
    let maxBlockPoints = 0;
    let maxBlockEfficiency = -100;
    let maxDigSuccess = 0;
    let maxDigEfficiency = -100;
    let maxSetEfficiency = -100;
    let maxTotalPoints = 0;

    Object.keys(byMatch).forEach(matchId => {
        const matchActions = byMatch[matchId];
        const match = matches.find(m => m.id === parseInt(matchId));
        if (!match) return;

        // Группируем по типам
        const byType = {};
        matchActions.forEach(action => {
            if (!byType[action.action_type]) {
                byType[action.action_type] = [];
            }
            byType[action.action_type].push(action);
        });

        const attacks = mergeActions(byType, ['AT', 'AH', 'AQ', 'AM', 'AO']);
        const serves = mergeActions(byType, ['SQ', 'SM']);
        const blocks = mergeActions(byType, ['BT', 'BH', 'BQ', 'BO']);
        const receptions = mergeActions(byType, ['RQ', 'RM']);
        const digs = mergeActions(byType, ['DT', 'DH', 'DQ', 'DO']);
        const sets = mergeActions(byType, ['ET', 'EH', 'EQ', 'EO']);

        // Атака - очки
        const attackPoints = attacks.filter(a => a.quality === '#').length;
        if (attackPoints > maxAttackPoints) {
            maxAttackPoints = attackPoints;
            bestMatchAttack = {
                match_id: parseInt(matchId),
                date: match.date || match.date_full || '',
                home_team: match.home_team || '',
                away_team: match.away_team || '',
                home_team_short: match.home_team_short || '',
                away_team_short: match.away_team_short || '',
                value: attackPoints,
                total: attacks.length,
            };
        }

        // Атака - эффективность
        const attackTotal = attacks.length;
        if (attackTotal > 0) {
            const attackKills = attacks.filter(a => a.quality === '#').length;
            const attackErrors = attacks.filter(a => a.quality === '=').length;
            const attackBlocked = attacks.filter(a => a.quality === '/').length;
            const efficiency = ((attackKills - attackErrors - attackBlocked) / attackTotal) * 100;

            if (efficiency > maxAttackEfficiency) {
                maxAttackEfficiency = efficiency;
                bestMatchAttackEff = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: Math.round(efficiency * 10) / 10,
                    total: attackTotal,
                    kills: attackKills,
                    errors: attackErrors,
                    blocked: attackBlocked,
                };
            }
        }

        // Подача - эйсы
        const serveAces = serves.filter(s => s.quality === '#' || s.rally_result === 'p').length;
        if (serveAces > maxServeAces) {
            maxServeAces = serveAces;
            bestMatchServe = {
                match_id: parseInt(matchId),
                date: match.date || match.date_full || '',
                home_team: match.home_team || '',
                away_team: match.away_team || '',
                home_team_short: match.home_team_short || '',
                away_team_short: match.away_team_short || '',
                value: serveAces,
                total: serves.length,
            };
        }

        // Подача - эффективность
        const serveTotal = serves.length;
        if (serveTotal > 0) {
            // Формула: ((#+!/)-(=)) / Tot
            const serveWon = serves.filter(s => ['#', '+', '!', '/'].includes(s.quality) || s.rally_result === 'p').length;
            const serveLost = serves.filter(s => s.quality === '=').length;
            const serveEfficiency = ((serveWon - serveLost) / serveTotal) * 100;

            if (serveEfficiency > maxServeEfficiency) {
                maxServeEfficiency = serveEfficiency;
                bestMatchServeEff = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: Math.round(serveEfficiency * 10) / 10,
                    aces: serveAces,
                    total: serveTotal,
                };
            }
        }

        // Приём - позитивный процент
        const receptionTotal = receptions.length;
        if (receptionTotal > 0) {
            const receptionPositive = receptions.filter(r => ['#', '+'].includes(r.quality)).length;
            const receptionPerfect = receptions.filter(r => r.quality === '#').length;
            const receptionPositivePct = (receptionPositive / receptionTotal) * 100;
            const receptionPerfectPct = (receptionPerfect / receptionTotal) * 100;
            
            // Формула: ((#+)-(=/)) / Tot
            const receptionWon = receptions.filter(r => ['#', '+'].includes(r.quality)).length;
            const receptionLost = receptions.filter(r => ['=', '/'].includes(r.quality)).length;
            const receptionEfficiency = ((receptionWon - receptionLost) / receptionTotal) * 100;

            // Позитивный процент
            if (receptionPositivePct > maxReceptionPct) {
                maxReceptionPct = receptionPositivePct;
                bestMatchReception = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: Math.round(receptionPositivePct * 10) / 10,
                    positive: receptionPositive,
                    perfect: receptionPerfect,
                    total: receptionTotal,
                };
            }
            
            // Отличный процент
            if (receptionPerfectPct > maxReceptionPerfectPct) {
                maxReceptionPerfectPct = receptionPerfectPct;
                bestMatchReceptionPerfect = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: Math.round(receptionPerfectPct * 10) / 10,
                    perfect: receptionPerfect,
                    positive: receptionPositive,
                    total: receptionTotal,
                };
            }
            
            // Эффективность приема
            if (receptionEfficiency > maxReceptionEfficiency) {
                maxReceptionEfficiency = receptionEfficiency;
                bestMatchReceptionEff = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: Math.round(receptionEfficiency * 10) / 10,
                    positive: receptionPositive,
                    perfect: receptionPerfect,
                    total: receptionTotal,
                };
            }
        }

        // Блок - блоки (как на главной странице: block_positive = # или +)
        const blockTotal = blocks.length;
        const blockKills = blocks.filter(b => b.quality === '#').length;
        const blockSoft = blocks.filter(b => b.quality === '+').length;
        const blockRemain = blocks.filter(b => b.quality === '/').length;
        const blockErrors = blocks.filter(b => b.quality === '=').length;
        const blockPositive = blockKills + blockSoft;
        
        if (blockTotal > 0) {
            // Очки на блоке
            if (blockPositive > maxBlockPoints) {
                maxBlockPoints = blockPositive;
                bestMatchBlock = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: blockPositive,
                    total: blockTotal,
                };
            }
            
            // Эффективность блока: (# + + - / - =) / total
            const blockEfficiency = ((blockKills + blockSoft - blockRemain - blockErrors) / blockTotal) * 100;
            if (blockEfficiency > maxBlockEfficiency) {
                maxBlockEfficiency = blockEfficiency;
                bestMatchBlockEff = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: Math.round(blockEfficiency * 10) / 10,
                    kills: blockKills,
                    soft: blockSoft,
                    remain: blockRemain,
                    errors: blockErrors,
                    total: blockTotal,
                };
            }
        }

        // Защита - успешные
        const digSuccess = digs.filter(d => ['#', '+', '!'].includes(d.quality)).length;
        if (digSuccess > maxDigSuccess) {
            maxDigSuccess = digSuccess;
            bestMatchDig = {
                match_id: parseInt(matchId),
                date: match.date || match.date_full || '',
                home_team: match.home_team || '',
                away_team: match.away_team || '',
                home_team_short: match.home_team_short || '',
                away_team_short: match.away_team_short || '',
                value: digSuccess,
                total: digs.length,
            };
        }

        // Защита - эффективность
        const digTotal = digs.length;
        if (digTotal > 0) {
            // Формула: ((#+/)-(=)) / Tot
            const digWon = digs.filter(d => ['#', '+', '/'].includes(d.quality)).length;
            const digLost = digs.filter(d => d.quality === '=').length;
            const digEfficiency = ((digWon - digLost) / digTotal) * 100;

            if (digEfficiency > maxDigEfficiency) {
                maxDigEfficiency = digEfficiency;
                bestMatchDigEff = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: Math.round(digEfficiency * 10) / 10,
                    success: digSuccess,
                    total: digTotal,
                };
            }
        }

        // Всего очков (атака + подача + блок)
        // Используем blockPositive (как на главной странице) вместо blockPoints
        const totalPoints = attackPoints + serveAces + blockPositive;
        if (totalPoints > maxTotalPoints) {
            maxTotalPoints = totalPoints;
            bestMatchTotalPoints = {
                match_id: parseInt(matchId),
                date: match.date || match.date_full || '',
                home_team: match.home_team || '',
                away_team: match.away_team || '',
                home_team_short: match.home_team_short || '',
                away_team_short: match.away_team_short || '',
                value: totalPoints,
                attack_points: attackPoints,
                serve_aces: serveAces,
                block_points: blockPositive,
            };
        }

        // Передачи - эффективность
        const setTotal = sets.length;
        if (setTotal > 0) {
            // Формула: ((#+)-(/=)) / Tot
            const setWon = sets.filter(s => ['#', '+'].includes(s.quality)).length;
            const setLost = sets.filter(s => ['/', '='].includes(s.quality)).length;
            const setEfficiency = ((setWon - setLost) / setTotal) * 100;
            const setExcellent = sets.filter(s => s.quality === '#').length;
            const setPositive = sets.filter(s => ['#', '+'].includes(s.quality)).length;

            if (setEfficiency > maxSetEfficiency) {
                maxSetEfficiency = setEfficiency;
                bestMatchSetEff = {
                    match_id: parseInt(matchId),
                    date: match.date || match.date_full || '',
                    home_team: match.home_team || '',
                    away_team: match.away_team || '',
                    home_team_short: match.home_team_short || '',
                    away_team_short: match.away_team_short || '',
                    value: Math.round(setEfficiency * 10) / 10,
                    excellent: setExcellent,
                    positive: setPositive,
                    total: setTotal,
                };
            }
        }
    });

    return {
        attack_points: bestMatchAttack,
        attack_efficiency: bestMatchAttackEff,
        serve_aces: bestMatchServe,
        serve_efficiency: bestMatchServeEff,
        reception: bestMatchReception,
        reception_perfect: bestMatchReceptionPerfect,
        reception_efficiency: bestMatchReceptionEff,
        block: bestMatchBlock,
        block_efficiency: bestMatchBlockEff,
        dig_success: bestMatchDig,
        dig_efficiency: bestMatchDigEff,
        set_efficiency: bestMatchSetEff,
        total_points: bestMatchTotalPoints,
    };
};

